import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowRight,
    Copy,
    Check,
    ExternalLink,
    QrCode as QrIcon,
    TrendingUp,
    MousePointerClick,
    Eye as EyeIcon,
    Clock,
    Sparkles,
    Award,
    User as UserIcon,
    FileText,
    BookOpen,
    Image as ImageIcon,
    Rocket,
    Search as SearchIcon,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import RepublishBanner from "@/components/RepublishBanner";
import IdentityScoreWidget from "@/components/IdentityScoreWidget";
import HealthMonitorPanel from "@/components/HealthMonitorPanel";
import { PROFILE_STATUS_STYLES, MODULE_ICON_TITLES, inr } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
}

function payLabel(state) {
    return ({
        unpaid: "Unpaid",
        advance_pending: "Advance Pending",
        advance_paid: "Advance Paid",
        balance_pending: "Balance Pending",
        fully_paid: "Fully Paid",
        failed: "Failed",
        refunded: "Refunded",
    }[state] || "Unknown");
}

function payBadgeCls(state) {
    return ({
        unpaid: "bg-slate-100 text-slate-700",
        advance_pending: "bg-yellow-100 text-yellow-800",
        advance_paid: "bg-blue-100 text-[#3A80F7]",
        balance_pending: "bg-yellow-100 text-yellow-800",
        fully_paid: "bg-green-100 text-[#16A34A]",
        failed: "bg-red-100 text-[#DC2626]",
        refunded: "bg-slate-100 text-slate-700",
    }[state] || "bg-slate-100 text-slate-700");
}

function severityCls(sev) {
    return ({
        info: "bg-[#EEF4FE] border-[#3A80F7]/25 text-[#0F172A]",
        warning: "bg-[#FEF3C7] border-[#F59E0B]/30 text-[#92400E]",
        success: "bg-[#E7F7EE] border-[#16A34A]/30 text-[#166534]",
        error: "bg-[#FEE2E2] border-[#DC2626]/30 text-[#991B1B]",
    }[sev] || "bg-slate-50 border-slate-200 text-slate-800");
}

function IdentityScoreCompact() {
    const [score, setScore] = useState(null);
    useEffect(() => {
        api.get("/me/identity-score/latest").then((r) => setScore(r.data)).catch(() => {});
    }, []);
    if (!score || score.score == null) {
        return (
            <div className="ddi-stat-card" data-testid="card-identity-score">
                <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-2">Digital Identity Score</div>
                <div className="ddi-stat-value text-[#94A3B8]">…</div>
                <div className="ddi-stat-label">Computing on first load.</div>
            </div>
        );
    }
    return (
        <div className="ddi-stat-card" data-testid="card-identity-score">
            <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">Digital Identity Score</div>
                <Sparkles size={14} className="text-[#3A80F7]" />
            </div>
            <div className="ddi-stat-value text-[#3A80F7]" data-testid="identity-score-compact">
                {score.score}<span className="text-[#94A3B8] text-[16px]">/100</span>
            </div>
            <div className="ddi-stat-label">{score.label}</div>
        </div>
    );
}


function DailyChart({ daily }) {
    const max = Math.max(1, ...daily.map((d) => d.count));
    return (
        <div className="flex items-end gap-1 h-16" data-testid="daily-views-chart">
            {daily.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                    <div
                        className="w-full rounded-sm bg-gradient-to-t from-[#3A80F7] to-[#5B99F9]"
                        style={{ height: `${Math.max(4, (d.count / max) * 100)}%`, minHeight: "4px" }}
                    />
                </div>
            ))}
        </div>
    );
}

const QUICK_ACTIONS = [
    { to: "/doctor/onboarding", icon: UserIcon, label: "Edit Profile" },
    { to: "/doctor/credentials", icon: Award, label: "Add Credential" },
    { to: "/doctor/gallery", icon: ImageIcon, label: "Upload Media" },
    { to: "/doctor/articles", icon: BookOpen, label: "Write Article" },
    { to: "/doctor/ai-studio", icon: Sparkles, label: "AI Studio" },
    { to: "/doctor/seo", icon: SearchIcon, label: "SEO" },
];

export default function DoctorDashboard() {
    const [bundle, setBundle] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [previewUrls, setPreviewUrls] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [copied, setCopied] = useState(false);
    const [pkgInfo, setPkgInfo] = useState(null);
    const [entitlements, setEntitlements] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            api.get("/me/dashboard"),
            api.get("/me/analytics/summary").catch(() => ({ data: { empty: true } })),
            api.get("/publish/preview-url").catch(() => ({ data: null })),
            api.get("/me/notifications").catch(() => ({ data: { items: [] } })),
            api.get("/me/entitlements").catch(() => ({ data: null })),
        ]).then(([db, an, pv, nt, ent]) => {
            setBundle(db.data);
            setAnalytics(an.data);
            setPreviewUrls(pv.data);
            setNotifications((nt.data.items || []).slice(0, 5));
            setEntitlements(ent.data);
            if (db.data?.doctor?.package_code) {
                api.get("/packages").then((r) => {
                    const p = (r.data || []).find((x) => x.code === db.data.doctor.package_code);
                    setPkgInfo(p);
                }).catch(() => {});
            }
        }).catch((e) => toast.error(errMsg(e, "Failed to load dashboard")));
    }, []);

    if (!bundle) {
        return (
            <DoctorShell>
                <div className="animate-fade-up" data-testid="dashboard-loading">
                    <div className="ddi-skeleton h-3 w-32 mb-3" />
                    <div className="ddi-skeleton h-8 w-64 mb-2" />
                    <div className="ddi-skeleton h-4 w-96 mb-6" />
                    {/* 4 stat cards row */}
                    <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="ddi-stat-card">
                                <div className="ddi-skeleton h-3 w-24 mb-3" />
                                <div className="ddi-skeleton h-8 w-20 mb-2" />
                                <div className="ddi-skeleton h-3 w-32" />
                            </div>
                        ))}
                    </div>
                    {/* Widget row */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
                        <div className="ddi-skeleton h-48 col-span-2" />
                        <div className="ddi-skeleton h-48" />
                    </div>
                    {/* Quick actions */}
                    <div className="ddi-card ddi-shadow-sm p-4 mb-6">
                        <div className="ddi-skeleton h-3 w-24 mb-3" />
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="ddi-skeleton h-20 rounded-xl" />
                            ))}
                        </div>
                    </div>
                </div>
            </DoctorShell>
        );
    }

    const d = bundle.doctor;
    const pending = bundle.pending_action;
    const badge = PROFILE_STATUS_STYLES[d.profile_status] || PROFILE_STATUS_STYLES.draft;
    const isPublished = d.profile_status === "published";

    const publicUrl = previewUrls?.public_url;
    const qrUrl = previewUrls?.qr_url;

    function copyUrl() {
        if (!publicUrl) return;
        navigator.clipboard.writeText(publicUrl).then(() => {
            setCopied(true);
            toast.success("Public URL copied");
            setTimeout(() => setCopied(false), 2000);
        });
    }

    // Group entitlements by module for completion center
    const moduleData = MODULE_ICON_TITLES.map((m) => {
        const items = (entitlements?.items || []).filter((i) => i.module_code === m.code);
        const total = items.length;
        const unlocked = items.filter((i) => i.unlocked).length;
        const lockCode = items.find((i) => !i.unlocked && i.lock_reason === "upgrade_needed")?.upgrade_to;
        return { ...m, total, unlocked, lockCode };
    });

    return (
        <DoctorShell doctor={d}>
            {/* Greeting */}
            <div className="mb-6" data-testid="dashboard-greeting">
                <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">{new Date().toDateString()}</div>
                <h1 className="text-3xl font-bold text-[#0F172A] mt-1 tracking-tight">
                    {greeting()}, {d?.full_name?.replace(/^Dr\.?\s*/i, "Dr. ")}
                </h1>
                <p className="text-[#64748B] mt-1 text-[14px]">
                    Here's a snapshot of your digital identity and what's next.
                </p>
            </div>

            <RepublishBanner />

            {/* Pending Action */}
            <div
                className={"rounded-2xl border p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4 " + severityCls(pending.severity)}
                data-testid="dashboard-pending-action"
            >
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1 font-semibold">Next step</div>
                    <div className="text-[16px] font-bold" data-testid="pending-action-title">{pending.title}</div>
                    <div className="text-[13px] mt-1 opacity-85">{pending.description}</div>
                </div>
                <Button
                    onClick={() => navigate(pending.cta_link)}
                    data-testid="pending-action-cta"
                    className="bg-[#3A80F7] hover:bg-[#2D6BE0] text-white"
                >
                    {pending.cta_label} <ArrowRight size={16} className="ml-2" />
                </Button>
            </div>

            {/* Overview scorecards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                <div className="ddi-stat-card" data-testid="card-profile-completion">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">Profile completion</div>
                        <UserIcon size={16} className="text-[#3A80F7]" />
                    </div>
                    <div className="ddi-stat-value" data-testid="profile-completion-value">
                        {bundle.profile_completion}<span className="text-[#94A3B8] text-[16px]">%</span>
                    </div>
                    <Progress value={bundle.profile_completion} className="mt-3 h-2" />
                </div>

                <div className="ddi-stat-card" data-testid="card-website-status">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">Website status</div>
                        <Rocket size={16} className="text-[#3A80F7]" />
                    </div>
                    <div className="mb-2">
                        <span className={"ddi-badge " + badge.cls}>{badge.label}</span>
                    </div>
                    {isPublished && publicUrl && (
                        <div className="mt-1 flex items-center gap-2 text-[12px]">
                            <a href={publicUrl} target="_blank" rel="noopener" className="text-[#3A80F7] hover:underline truncate max-w-[160px] font-mono" data-testid="public-url-link">
                                {publicUrl.replace(/^https?:\/\//, "")}
                            </a>
                            <button onClick={copyUrl} data-testid="copy-public-url" className="text-[#3A80F7]">
                                {copied ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                        </div>
                    )}
                </div>

                <div className="ddi-stat-card" data-testid="card-payment-status">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">Payment status</div>
                    </div>
                    <span className={"ddi-badge " + payBadgeCls(d.payment_state)} data-testid="payment-state-badge">
                        {payLabel(d.payment_state)}
                    </span>
                    <div className="mt-3 text-[#64748B] text-[13px]">
                        Paid: <span className="font-semibold text-[#0F172A]">{inr(bundle.amount_paid_total_inr)}</span>
                    </div>
                </div>

                {/* Identity Score compact card in the 4-scorecard row */}
                <IdentityScoreCompact />
            </div>

            {/* Full Identity Score widget + Health Monitor row */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
                <IdentityScoreWidget />
                <HealthMonitorPanel />
            </div>

            {/* Quick actions */}
            <Card className="border-[#E5E9F2] mb-6 ddi-shadow-sm">
                <CardContent className="py-4">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold">Quick actions</div>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                        {QUICK_ACTIONS.map((a) => {
                            const Icon = a.icon;
                            return (
                                <button
                                    key={a.to}
                                    onClick={() => navigate(a.to)}
                                    className="rounded-xl border border-[#E5E9F2] bg-white hover:border-[#3A80F7] hover:bg-[#EEF4FE] transition-all p-3 flex flex-col items-center gap-2 group"
                                    data-testid={`quick-action-${a.label.replace(/\s/g, "-").toLowerCase()}`}
                                >
                                    <div className="h-9 w-9 rounded-lg bg-[#EEF4FE] text-[#3A80F7] flex items-center justify-center group-hover:bg-[#3A80F7] group-hover:text-white transition-colors">
                                        <Icon size={16} />
                                    </div>
                                    <span className="text-[12px] font-medium text-[#334155]">{a.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* QR + Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {isPublished && qrUrl ? (
                    <Card className="border-[#E5E9F2] ddi-shadow-sm" data-testid="card-qr">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-[13px] flex items-center gap-2 text-[#334155]">
                                <QrIcon size={14} className="text-[#3A80F7]" /> QR code
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <img src={qrUrl} alt="QR code" className="w-32 h-32 border border-[#E5E9F2] rounded-lg bg-white p-2" data-testid="dashboard-qr-image" />
                            <a href={qrUrl} download="qr.png" className="inline-flex items-center gap-1 text-[12px] text-[#3A80F7] mt-2 hover:underline" data-testid="dashboard-qr-download">
                                Download <ExternalLink size={11} />
                            </a>
                        </CardContent>
                    </Card>
                ) : (
                    <Card className="border-dashed border-[#E5E9F2] bg-[#F5F7FB]" data-testid="card-qr-pending">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-[13px] flex items-center gap-2 text-[#64748B]">
                                <QrIcon size={14} /> QR code
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-[12px] text-[#64748B]">
                            Your QR appears here after your profile is <b className="text-[#334155]">published</b>.
                            <div className="mt-2">
                                <a href="/doctor/publish" className="text-[12px] text-[#3A80F7] hover:underline font-semibold">
                                    Open publish flow →
                                </a>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <Card className={"border-[#E5E9F2] ddi-shadow-sm " + "lg:col-span-2"} data-testid="card-analytics">
                    <CardHeader className="pb-2 flex flex-row justify-between items-center">
                        <CardTitle className="text-[13px] flex items-center gap-2 text-[#334155]">
                            <TrendingUp size={14} className="text-[#3A80F7]" /> Visitor analytics
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {analytics?.empty ? (
                            <div className="py-8 text-center text-[#64748B] text-[13px]" data-testid="analytics-empty">
                                No visits yet. Once you publish your page, views, QR scans and CTA clicks appear here.
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                    {[
                                        { key: "views-total", label: "Total views", value: analytics?.views_total || 0, icon: EyeIcon },
                                        { key: "views-7d", label: "Views · 7d", value: analytics?.views_7d || 0, icon: TrendingUp },
                                        { key: "qr-scans", label: "QR scans", value: analytics?.qr_scans_total || 0, icon: QrIcon },
                                        { key: "cta-clicks", label: "CTA clicks", value: analytics?.cta_clicks_total || 0, icon: MousePointerClick },
                                    ].map((s) => {
                                        const SIcon = s.icon;
                                        return (
                                            <div key={s.key}>
                                                <div className="flex items-center gap-1.5 mb-1">
                                                    <SIcon size={12} className="text-[#94A3B8]" />
                                                    <span className="text-[11px] text-[#64748B]">{s.label}</span>
                                                </div>
                                                <div className="text-[22px] font-bold text-[#0F172A]" data-testid={`analytics-${s.key}`}>{s.value}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {analytics?.daily && <DailyChart daily={analytics.daily} />}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Module Completion Center */}
            <Card className="border-[#E5E9F2] mb-6 ddi-shadow-sm" data-testid="module-completion-center">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                    <CardTitle className="text-[15px]">Profile completion center</CardTitle>
                    {pkgInfo && <span className="ddi-chip ddi-chip-primary">{pkgInfo.name}</span>}
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {moduleData.map((m) => {
                            const pct = m.total ? Math.round((m.unlocked / m.total) * 100) : 0;
                            const stat = m.total === 0 ? "empty" : m.unlocked === m.total ? "unlocked" : m.unlocked === 0 ? "locked" : "partial";
                            const statChip = stat === "unlocked" ? "ddi-chip-success" : stat === "partial" ? "ddi-chip-warning" : "ddi-chip-neutral";
                            const statLabel = stat === "unlocked" ? "All unlocked" : stat === "partial" ? "Partial" : stat === "locked" ? "Locked" : "—";
                            return (
                                <div key={m.code} className="rounded-xl border border-[#E5E9F2] p-3.5 bg-white hover:border-[#3A80F7] transition-colors" data-testid={`module-tile-${m.code}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="h-8 w-8 rounded-lg bg-[#EEF4FE] text-[#3A80F7] flex items-center justify-center text-[11px] font-bold">{m.code}</div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[13px] font-semibold text-[#0F172A] truncate">{m.name}</div>
                                            <div className="text-[11px] text-[#64748B]">{m.unlocked} of {m.total} features</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                                            <div className={"h-full " + (stat === "unlocked" ? "bg-[#16A34A]" : stat === "partial" ? "bg-[#F59E0B]" : "bg-[#94A3B8]")} style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className={"ddi-chip " + statChip + " text-[10px]"}>{statLabel}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Recent activity */}
            <Card className="border-[#E5E9F2] ddi-shadow-sm" data-testid="card-activity">
                <CardHeader className="pb-3">
                    <CardTitle className="text-[13px] flex items-center gap-2 text-[#334155]">
                        <Clock size={14} className="text-[#3A80F7]" /> Recent activity
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {notifications.length === 0 ? (
                        <div className="text-[13px] text-[#64748B] py-4">No recent notifications.</div>
                    ) : (
                        <ul className="divide-y divide-[#EEF1F6]">
                            {notifications.map((n) => (
                                <li key={n.id} className="py-3 flex items-start gap-3">
                                    <div className="mt-1 w-2 h-2 rounded-full bg-[#3A80F7]" />
                                    <div className="flex-1">
                                        <div className="text-[13px] font-semibold text-[#0F172A]">{n.title}</div>
                                        <div className="text-[12px] text-[#64748B] mt-0.5">{n.body}</div>
                                    </div>
                                    <div className="text-[10px] text-[#94A3B8]">
                                        {new Date(n.created_at).toLocaleDateString()}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
